import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from "axios";
import { AuthenticationManager } from "@playit/shared-authentication";
import type { Store } from "@reduxjs/toolkit";
import {
  setLogout,
  setReauthenticationSuccess,
} from "../app/slices/AuthenticationSlice";
import type { RootState } from "../app/Store";
import { AuthenticationService } from "./AuthenticationService";

type AuthStore = Pick<Store<RootState>, "dispatch" | "getState">;

type RetriableRequestConfig = InternalAxiosRequestConfig & {
  _authRefreshRetry?: boolean;
};

let interceptorId: number | null = null;
let refreshPromise: Promise<string | null> | null = null;

const AUTHENTICATE_PATH = "/v2/authenticate";
const REFRESH_PATH = "/v2/authenticate/refreshtoken";

function normalizeUrl(url?: string): string {
  return (url ?? "").toLowerCase();
}

function isRefreshRequest(config?: AxiosRequestConfig): boolean {
  const url = normalizeUrl(config?.url);
  return url.includes(REFRESH_PATH);
}

function isLoginRequest(config?: AxiosRequestConfig): boolean {
  const url = normalizeUrl(config?.url);
  return url.includes(AUTHENTICATE_PATH) && !url.includes(REFRESH_PATH);
}

function isPlayItApiRequest(config?: AxiosRequestConfig): boolean {
  const url = normalizeUrl(config?.url);
  const mainApi = normalizeUrl(process.env.REACT_APP_PLAYIT_MAINAPI);
  const creatorApi = normalizeUrl(process.env.REACT_APP_PLAYIT_CREATORAPI);

  return (
    (!!mainApi && url.startsWith(mainApi)) ||
    (!!creatorApi && url.startsWith(creatorApi))
  );
}

function setAuthorizationHeader(
  config: InternalAxiosRequestConfig,
  token: string
) {
  const headers = AxiosHeaders.from(config.headers);
  headers.set("Authorization", `Bearer ${token}`);
  config.headers = headers;
}

async function refreshAuthToken(store: AuthStore): Promise<string | null> {
  const response = await AuthenticationService.AuthenticateWithRefreshToken();
  const token = response?.jwt?.token;
  const refreshToken = response?.jwt?.refreshToken;

  if (!response || !token || !refreshToken) {
    return null;
  }

  store.dispatch(
    setReauthenticationSuccess({
      jwt: token,
      organizationIDs: response.organizationIDs,
      userID: response.userID,
      refreshToken,
    })
  );
  AuthenticationManager.SetToken(token);

  return token;
}

async function logoutAfterRefreshFailure(store: AuthStore) {
  store.dispatch(setLogout());
  await AuthenticationService.Logout();
}

function shouldAttemptRefresh(error: AxiosError): boolean {
  const config = error.config as RetriableRequestConfig | undefined;

  return (
    error.response?.status === 401 &&
    !!config &&
    !config._authRefreshRetry &&
    isPlayItApiRequest(config) &&
    !isLoginRequest(config) &&
    !isRefreshRequest(config)
  );
}

export function installAuthRefreshInterceptor(store: AuthStore) {
  if (interceptorId !== null) return;

  interceptorId = axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      if (!shouldAttemptRefresh(error)) {
        return Promise.reject(error);
      }

      const originalRequest = error.config as RetriableRequestConfig;
      originalRequest._authRefreshRetry = true;

      if (!refreshPromise) {
        refreshPromise = refreshAuthToken(store).finally(() => {
          refreshPromise = null;
        });
      }

      const token = await refreshPromise.catch(() => null);

      if (!token) {
        await logoutAfterRefreshFailure(store);
        return Promise.reject(error);
      }

      setAuthorizationHeader(originalRequest, token);
      return axios(originalRequest);
    }
  );
}

export function resetAuthRefreshInterceptorForTests() {
  if (interceptorId !== null) {
    axios.interceptors.response.eject(interceptorId);
  }
  interceptorId = null;
  refreshPromise = null;
}
